import { useState, useMemo } from 'react';
import {
  Cpu,
  ChevronDown,
  ChevronRight,
  Check,
  Star,
  Activity,
  FlaskConical,
  X,
} from 'lucide-react';
import {
  MODEL_REGISTRY,
  DEFAULT_MODEL_ID,
  getStoredModelPref,
  setStoredModelPref,
  getRoFormerModels,
  getProductionModels,
  type TransformerModel,
  type ModelArchitecture,
  type ModelTier,
  type StemTarget,
} from '../../lib/model-registry';
import { ModelSpectrum, StemSpectrum } from './SpectrumVisualizer';

// ─── Architecture meta ──────────────────────────────────────────────────────

const ARCH_META: Record<ModelArchitecture, { label: string; color: string }> = {
  roformer:            { label: 'RoFormer',         color: '#00FFFF' },
  band_split_roformer: { label: 'BS-RoFormer',     color: '#00FFFF' },
  mel_band_roformer:   { label: 'Mel-RoFormer',    color: '#00E5CC' },
  htdemucs:            { label: 'HTDemucs',         color: '#A78BFA' },
  mdx_net:             { label: 'MDX-Net',          color: '#FBBF24' },
  scnet:               { label: 'SCNet',            color: '#F97316' },
  segm_models:         { label: 'SegM',             color: '#EC4899' },
};

const TIER_META: Record<ModelTier, { label: string; icon: typeof Star; color: string }> = {
  production:   { label: 'Production', icon: Star,         color: '#39FF14' },
  experimental: { label: 'Experimental', icon: FlaskConical, color: '#FBBF24' },
  research:     { label: 'Research', icon: Activity,      color: '#EC4899' },
};

// ─── Main Component ─────────────────────────────────────────────────────────

interface ModelSelectorProps {
  onSelect: (modelId: string) => void;
  onClose: () => void;
  currentModel?: string;
  compact?: boolean;
}

export function ModelSelector({ onSelect, onClose, currentModel }: ModelSelectorProps) {
  const [selected, setSelected] = useState(currentModel || getStoredModelPref());
  const [filter, setFilter] = useState<'all' | 'roformer' | 'production' | 'ensemble'>('all');
  const [expandedModel, setExpandedModel] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'sdr' | 'speed' | 'name'>('sdr');

  const filtered = useMemo(() => {
    let models = MODEL_REGISTRY;
    if (filter === 'roformer') models = getRoFormerModels();
    else if (filter === 'production') models = getProductionModels();
    else if (filter === 'ensemble') models = MODEL_REGISTRY.filter(m => m.stems.length >= 4);

    if (sortBy === 'sdr') {
      models = [...models].sort((a, b) => (b.sdr_benchmark.vocals ?? 0) - (a.sdr_benchmark.vocals ?? 0));
    } else if (sortBy === 'speed') {
      const speedOrder = { fast: 0, medium: 1, slow: 2 };
      models = [...models].sort((a, b) => speedOrder[a.inference_speed] - speedOrder[b.inference_speed]);
    } else {
      models = [...models].sort((a, b) => a.name.localeCompare(b.name));
    }
    return models;
  }, [filter, sortBy]);

  function handleConfirm() {
    setStoredModelPref(selected);
    onSelect(selected);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm model-selector-in">
      <div className="w-full max-w-2xl max-h-[85vh] bg-[#0a0e1a] border border-white/[0.12] rounded-xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/[0.08] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#00FFFF]/10 flex items-center justify-center">
              <Cpu size={16} className="text-[#00FFFF]" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white tracking-tight">Transformer Model Selection</h2>
              <p className="text-[10px] text-slate-400 mt-0.5">RoFormer-style models are default. Pick from HuggingFace transformers.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/[0.06] text-slate-400 hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b border-white/[0.06] flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-1.5">
            {(['all', 'roformer', 'production', 'ensemble'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm transition-all ${
                  filter === f
                    ? 'bg-[#00FFFF]/15 text-[#00FFFF] border border-[#00FFFF]/40'
                    : 'text-slate-400 border border-white/[0.08] hover:border-white/20 hover:text-white'
                }`}
              >
                {f === 'roformer' ? 'RoFormer' : f === 'ensemble' ? '4+ Stems' : f}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 text-[9px] font-mono text-slate-400">
            <span className="uppercase tracking-wider">Sort:</span>
            {(['sdr', 'speed', 'name'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setSortBy(s)}
                className={`px-2 py-0.5 rounded transition-colors ${sortBy === s ? 'text-white bg-white/[0.08]' : 'hover:text-white'}`}
              >
                {s === 'sdr' ? 'SDR' : s}
              </button>
            ))}
          </div>
        </div>

        {/* Model List */}
        <div className="flex-1 overflow-y-auto scrollbar-thin px-4 py-3 space-y-1.5">
          {filtered.map((model) => (
            <ModelCard
              key={model.id}
              model={model}
              isSelected={selected === model.id}
              isDefault={model.id === DEFAULT_MODEL_ID}
              isExpanded={expandedModel === model.id}
              onSelect={() => setSelected(model.id)}
              onToggleExpand={() => setExpandedModel(expandedModel === model.id ? null : model.id)}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-white/[0.08] flex items-center justify-between">
          <div className="text-[10px] font-mono text-slate-400">
            {filtered.length} models available | selected: <span className="text-[#00FFFF]">{MODEL_REGISTRY.find(m => m.id === selected)?.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-[11px] text-slate-400 hover:text-white border border-white/[0.1] rounded transition-colors">
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-1.5 text-[11px] font-semibold bg-[#00FFFF]/15 text-[#00FFFF] border border-[#00FFFF]/40 rounded hover:bg-[#00FFFF]/25 transition-all inline-flex items-center gap-1.5"
            >
              <Check size={11} /> Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Model Card ─────────────────────────────────────────────────────────────

function ModelCard({
  model,
  isSelected,
  isDefault,
  isExpanded,
  onSelect,
  onToggleExpand,
}: {
  model: TransformerModel;
  isSelected: boolean;
  isDefault: boolean;
  isExpanded: boolean;
  onSelect: () => void;
  onToggleExpand: () => void;
}) {
  const archMeta = ARCH_META[model.architecture];
  const tierMeta = TIER_META[model.tier];
  const topSdr = Math.max(...Object.values(model.sdr_benchmark));

  return (
    <div
      className={`rounded-lg border transition-all duration-200 ${
        isSelected
          ? 'border-[#00FFFF]/50 bg-[#00FFFF]/[0.04] shadow-[0_0_20px_rgba(0,255,255,0.06)]'
          : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12] hover:bg-white/[0.03]'
      }`}
    >
      {/* Main row */}
      <div className="px-4 py-3 flex items-center gap-3 cursor-pointer" onClick={onSelect}>
        {/* Radio */}
        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
          isSelected ? 'border-[#00FFFF] bg-[#00FFFF]/20' : 'border-slate-500'
        }`}>
          {isSelected && <div className="w-2 h-2 rounded-full bg-[#00FFFF]" />}
        </div>

        {/* Model info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[12px] font-semibold text-white">{model.name}</span>
            {isDefault && (
              <span className="text-[8px] font-bold uppercase tracking-[0.2em] bg-[#00FFFF]/15 text-[#00FFFF] px-1.5 py-0.5 rounded">
                Default
              </span>
            )}
            {model.rotary_embedding && (
              <span className="text-[8px] font-bold uppercase tracking-[0.2em] bg-[#00FFFF]/10 text-[#00FFFF]/70 px-1.5 py-0.5 rounded border border-[#00FFFF]/20">
                RoPE
              </span>
            )}
            <span
              className="text-[8px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded"
              style={{ color: tierMeta.color, background: `${tierMeta.color}15`, border: `1px solid ${tierMeta.color}30` }}
            >
              {tierMeta.label}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-400">
            <span style={{ color: archMeta.color }} className="font-mono font-semibold">{archMeta.label}</span>
            <span className="text-slate-500">|</span>
            <span>{model.stems.length} stems</span>
            <span className="text-slate-500">|</span>
            <span>{model.params_millions}M params</span>
            <span className="text-slate-500">|</span>
            <span className={model.inference_speed === 'fast' ? 'text-emerald-300' : model.inference_speed === 'slow' ? 'text-red-300' : 'text-amber-300'}>
              {model.inference_speed}
            </span>
          </div>
        </div>

        {/* SDR badge */}
        <div className="text-right shrink-0">
          <div className="text-[11px] font-bold text-white tabular-nums">{topSdr.toFixed(2)} dB</div>
          <div className="text-[8px] text-slate-400 uppercase tracking-wider">Peak SDR</div>
        </div>

        {/* Expand */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
          className="p-1 rounded hover:bg-white/[0.06] text-slate-400 hover:text-white transition-colors"
        >
          {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
      </div>

      {/* Expanded detail */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-1 border-t border-white/[0.04] space-y-3 model-detail-in">
          <p className="text-[11px] text-slate-300 leading-relaxed">{model.description}</p>

          {/* Frequency Spectrum Coverage */}
          <div>
            <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5">Frequency Coverage (Mel-Scale)</div>
            <ModelSpectrum model={model} height={40} animated={true} />
          </div>

          {/* Stem Separation Bands */}
          <div>
            <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5">Stem Frequency Bands</div>
            <StemSpectrum stems={model.stems} height={56} showLabels={true} compact={false} />
          </div>

          {/* SDR Benchmarks */}
          <div>
            <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5">SDR Benchmarks (dB)</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(model.sdr_benchmark).map(([stem, sdr]) => (
                <SdrBar key={stem} stem={stem as StemTarget} sdr={sdr} />
              ))}
            </div>
          </div>

          {/* Strengths & Weaknesses */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-emerald-300 mb-1">Strengths</div>
              <div className="flex flex-wrap gap-1">
                {model.strengths.map((s) => (
                  <span key={s} className="text-[9px] font-mono text-emerald-200/80 bg-emerald-400/10 px-1.5 py-0.5 rounded">{s}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-red-300 mb-1">Weaknesses</div>
              <div className="flex flex-wrap gap-1">
                {model.weaknesses.map((w) => (
                  <span key={w} className="text-[9px] font-mono text-red-200/80 bg-red-400/10 px-1.5 py-0.5 rounded">{w}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Technical specs */}
          <div className="grid grid-cols-4 gap-2 text-[9px] font-mono">
            <div className="bg-white/[0.03] rounded px-2 py-1.5">
              <div className="text-slate-500">Memory</div>
              <div className="text-slate-200 mt-0.5">{model.memory_gb} GB</div>
            </div>
            <div className="bg-white/[0.03] rounded px-2 py-1.5">
              <div className="text-slate-500">Attention</div>
              <div className="text-slate-200 mt-0.5 truncate">{model.attention_type.replace(/_/g, ' ')}</div>
            </div>
            <div className="bg-white/[0.03] rounded px-2 py-1.5">
              <div className="text-slate-500">Domain</div>
              <div className="text-slate-200 mt-0.5">{model.frequency_domain ? 'Frequency' : 'Time+Freq'}</div>
            </div>
            <div className="bg-white/[0.03] rounded px-2 py-1.5">
              <div className="text-slate-500">RoPE</div>
              <div className={`mt-0.5 ${model.rotary_embedding ? 'text-[#00FFFF]' : 'text-slate-400'}`}>
                {model.rotary_embedding ? 'Yes' : 'No'}
              </div>
            </div>
          </div>

          {model.huggingface_ref && (
            <div className="text-[9px] font-mono text-slate-400">
              HuggingFace: <span className="text-blue-300">{model.huggingface_ref}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── SDR Bar ────────────────────────────────────────────────────────────────

function SdrBar({ stem, sdr }: { stem: StemTarget; sdr: number }) {
  const pct = Math.min((sdr / 12) * 100, 100);
  const color = sdr >= 10 ? '#39FF14' : sdr >= 9 ? '#00FFFF' : sdr >= 8 ? '#FBBF24' : '#F97316';

  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <span className="text-[9px] text-slate-400 w-[60px] truncate">{stem}</span>
      <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-[9px] font-mono tabular-nums" style={{ color }}>{sdr.toFixed(1)}</span>
    </div>
  );
}

// ─── Compact inline selector (for sidebar / control plane) ──────────────────

export function ModelSelectorInline({
  currentModel,
  onOpenFull,
}: {
  currentModel: string;
  onOpenFull: () => void;
}) {
  const model = MODEL_REGISTRY.find(m => m.id === currentModel);
  if (!model) return null;

  const archMeta = ARCH_META[model.architecture];

  return (
    <button
      onClick={onOpenFull}
      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md border border-white/60 bg-white/[0.02] hover:border-white hover:bg-white/[0.05] transition-all group"
    >
      <Cpu size={12} className="text-[#00FFFF]/60 group-hover:text-[#00FFFF] transition-colors shrink-0" />
      <div className="flex-1 min-w-0 text-left">
        <div className="text-[10px] font-semibold text-white truncate">{model.name}</div>
        <div className="text-[9px] font-mono" style={{ color: archMeta.color }}>{archMeta.label} | {model.stems.length}s | {model.inference_speed}</div>
      </div>
      <ChevronRight size={11} className="text-slate-500 group-hover:text-[#00FFFF] transition-colors shrink-0" />
    </button>
  );
}
